import { Pencil, ShieldCheck } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import {
  useCurrentUser,
  useRequestEmailChange,
  useRequestPhoneChange,
  useUpdateMe,
  useVerifyPhoneChange,
} from "@/api/hooks";
import { PhoneInput } from "@/components/PhoneInput";
import { formatPhoneForDisplay } from "@/lib/phoneFormat";

/** Self-service profile page.
 *
 *  Display name is editable inline (no verification). Email + phone
 *  changes go through verified flows:
 *
 *  - Email: server emails a confirmation link to the NEW address.
 *    Click the link (while still signed in) to apply the change.
 *  - Phone: server SMS-OTPs the NEW number via Twilio Verify. Enter
 *    the code to apply the change.
 *
 *  The admin-only escape hatch on /users (pencilout_admin patches
 *  email/phone directly) is for support cases — typo fixes, lost
 *  number recovery — not the main path. */
export function ProfilePage() {
  const me = useCurrentUser();

  if (me.isLoading) {
    return <p className="text-sm text-graphite-soft">Loading profile…</p>;
  }
  if (!me.data) {
    return (
      <p className="text-sm text-red-pencil">Couldn't load your profile. Try signing in again.</p>
    );
  }

  const user = me.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Profile</h1>
        <p className="mt-1 max-w-2xl text-sm text-graphite-soft">
          Your sign-in identity. Email + phone changes need verification — we send a confirmation
          link to the new email and an SMS code to the new number.
        </p>
      </div>

      <DisplayNameCard currentName={user.display_name} />

      <EmailCard currentEmail={user.email ?? null} />

      <PhoneCard
        currentPhone={user.phone_number ?? null}
        isVerified={user.phone_verified ?? false}
        canRemove={Boolean(user.email)}
      />

      <div className="rounded border border-rule bg-paper-warm p-4 text-xs text-graphite-soft">
        <p>
          <strong>Why verification?</strong> Your email + phone are how you sign in. Confirming on
          the new address before we cut over keeps a typo from locking you out.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Display name — inline editable
// ---------------------------------------------------------------

function DisplayNameCard({ currentName }: { currentName: string }) {
  const update = useUpdateMe();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentName);

  function onCancel() {
    setEditing(false);
    setDraft(currentName);
    update.reset();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!draft.trim() || draft.trim() === currentName) {
      onCancel();
      return;
    }
    try {
      await update.mutateAsync({ display_name: draft.trim() });
      setEditing(false);
    } catch {
      /* error surfaces via update.error below */
    }
  }

  return (
    <Card title="Display name">
      {editing ? (
        <form onSubmit={onSubmit} className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 rounded border border-rule bg-paper-warm px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
          />
          <button
            type="submit"
            disabled={update.isPending || !draft.trim()}
            className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft disabled:opacity-40"
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-rule px-3 py-1.5 font-mono text-xs text-graphite-soft hover:border-graphite hover:text-graphite"
          >
            Cancel
          </button>
          {update.error && (
            <p className="basis-full text-xs text-red-pencil">
              {update.error instanceof ApiError ? update.error.detail : "Could not save."}
            </p>
          )}
        </form>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-sm">{currentName}</span>
          <button
            type="button"
            onClick={() => {
              setDraft(currentName);
              setEditing(true);
            }}
            className="inline-flex items-center gap-1 rounded p-1.5 text-graphite-soft hover:bg-paper hover:text-graphite"
            title="Edit display name"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden /> Edit
          </button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------
// Email — request-change → confirm via clicked link
// ---------------------------------------------------------------

function EmailCard({ currentEmail }: { currentEmail: string | null }) {
  const request = useRequestEmailChange();
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);

  function onCancel() {
    setEditing(false);
    setNewEmail("");
    request.reset();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    try {
      await request.mutateAsync({ new_email: trimmed });
      setSentTo(trimmed);
      setEditing(false);
      setNewEmail("");
    } catch {
      /* error surfaces below */
    }
  }

  return (
    <Card title="Email">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-sm">{currentEmail ?? "—"}</span>
        {!editing && (
          <button
            type="button"
            onClick={() => {
              setSentTo(null);
              setEditing(true);
            }}
            className="inline-flex items-center gap-1 rounded p-1.5 text-graphite-soft hover:bg-paper hover:text-graphite"
            title="Change email"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden /> {currentEmail ? "Change" : "Add"}
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={onSubmit} className="mt-3 space-y-2">
          <label className="block">
            <span className="label-mono">New email</span>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="mt-1 w-full rounded border border-rule bg-paper-warm px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
            />
          </label>
          <p className="text-xs text-graphite-soft">
            We'll send a confirmation link to that address. Click it (while still signed in here) to
            swap your sign-in email.
          </p>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={request.isPending || !newEmail.trim()}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft disabled:opacity-40"
            >
              {request.isPending ? "Sending…" : "Send confirmation link"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-rule px-3 py-1.5 font-mono text-xs text-graphite-soft hover:border-graphite hover:text-graphite"
            >
              Cancel
            </button>
          </div>
          {request.error && (
            <p className="text-xs text-red-pencil">
              {request.error instanceof ApiError ? request.error.detail : "Could not send."}
            </p>
          )}
        </form>
      )}

      {sentTo && !editing && (
        <p className="mt-3 rounded border border-pencil-yellow bg-pencil-yellow-soft px-3 py-2 text-xs text-graphite">
          We sent a confirmation link to <strong>{sentTo}</strong>. Click it to finish the change.
          The link expires in 30 minutes. (If the address was already in use, the link won't arrive
          — try a different one.)
        </p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------
// Phone — request OTP → enter code → swap
// ---------------------------------------------------------------

function PhoneCard({
  currentPhone,
  isVerified,
  canRemove: _canRemove,
}: {
  currentPhone: string | null;
  isVerified: boolean;
  /** When true, the user has another contact method (email) so they
   *  could in theory remove their phone. v1 doesn't expose remove
   *  here — admin escape hatch only — so the prop is unused but
   *  kept for future. */
  canRemove: boolean;
}) {
  const request = useRequestPhoneChange();
  const verify = useVerifyPhoneChange();
  const [stage, setStage] = useState<"view" | "enter-phone" | "enter-code">("view");
  const [newPhone, setNewPhone] = useState("");
  const [code, setCode] = useState("");

  function reset() {
    setStage("view");
    setNewPhone("");
    setCode("");
    request.reset();
    verify.reset();
  }

  async function onSubmitPhone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = newPhone.trim();
    if (!trimmed) return;
    try {
      await request.mutateAsync({ new_phone: trimmed });
      setStage("enter-code");
    } catch {
      /* error surfaces below */
    }
  }

  async function onSubmitCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!code.trim() || !newPhone.trim()) return;
    try {
      await verify.mutateAsync({
        new_phone: newPhone.trim(),
        code: code.trim(),
      });
      reset();
    } catch {
      /* error surfaces below */
    }
  }

  return (
    <Card title="Phone">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-sm">
          {currentPhone ? formatPhoneForDisplay(currentPhone) : "—"}
          {currentPhone && isVerified && (
            <span className="ml-2 inline-flex items-center gap-1 rounded bg-pencil-yellow-soft px-1.5 py-0.5 font-mono text-[10px] text-graphite">
              <ShieldCheck className="h-3 w-3" aria-hidden /> verified
            </span>
          )}
          {currentPhone && !isVerified && (
            <span className="ml-2 rounded bg-paper px-1.5 py-0.5 font-mono text-[10px] text-graphite-soft">
              not yet verified
            </span>
          )}
        </span>
        {stage === "view" && (
          <button
            type="button"
            onClick={() => setStage("enter-phone")}
            className="inline-flex items-center gap-1 rounded p-1.5 text-graphite-soft hover:bg-paper hover:text-graphite"
            title="Change phone"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden /> {currentPhone ? "Change" : "Add"}
          </button>
        )}
      </div>

      {stage === "enter-phone" && (
        <form onSubmit={onSubmitPhone} className="mt-3 space-y-2">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: <PhoneInput> renders the input internally as a descendant of this <label>; the DOM association works at runtime even though biome can't see through the component boundary. */}
          <label className="block">
            <span className="label-mono">New phone</span>
            <PhoneInput
              value={newPhone}
              onChange={setNewPhone}
              placeholder="(704) 555-1234"
              required
              className="mt-1 w-full rounded border border-rule bg-paper-warm px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
              autoFocus
            />
          </label>
          <p className="text-xs text-graphite-soft">
            We'll text a 6-digit code to that number. Enter it on the next step to swap.
          </p>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={request.isPending || !newPhone.trim()}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft disabled:opacity-40"
            >
              {request.isPending ? "Sending…" : "Send code"}
            </button>
            <button
              type="button"
              onClick={reset}
              className="rounded border border-rule px-3 py-1.5 font-mono text-xs text-graphite-soft hover:border-graphite hover:text-graphite"
            >
              Cancel
            </button>
          </div>
          {request.error && (
            <p className="text-xs text-red-pencil">
              {request.error instanceof ApiError ? request.error.detail : "Could not send."}
            </p>
          )}
        </form>
      )}

      {stage === "enter-code" && (
        <form onSubmit={onSubmitCode} className="mt-3 space-y-2">
          <p className="text-xs text-graphite-soft">
            Code sent to <strong className="font-mono">{newPhone}</strong>. (If that number was
            already in use, the code won't arrive — try a different one.)
          </p>
          <label className="block">
            <span className="label-mono">6-digit code</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              required
              maxLength={8}
              className="mt-1 w-full rounded border border-rule bg-paper-warm px-2 py-1.5 font-mono text-sm tracking-widest focus:border-graphite focus:outline-none"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={verify.isPending || !code.trim()}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft disabled:opacity-40"
            >
              {verify.isPending ? "Verifying…" : "Confirm"}
            </button>
            <button
              type="button"
              onClick={reset}
              className="rounded border border-rule px-3 py-1.5 font-mono text-xs text-graphite-soft hover:border-graphite hover:text-graphite"
            >
              Cancel
            </button>
          </div>
          {verify.error && (
            <p className="text-xs text-red-pencil">
              {verify.error instanceof ApiError ? verify.error.detail : "Could not verify."}
            </p>
          )}
        </form>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </div>
  );
}
